import React, { useState, useEffect } from 'react'
import { observer } from 'mobx-react-lite'
import { FurnitureStoreProvider, Scene, SimpleCabinetFactory, Door, Drawer, Hardware, useFurnitureStore } from '@fabrick/components'
import { PropertiesPanelStoreProvider } from './stores/usePropertiesPanelStore'
import { UIStoreProvider, useUIStore } from './stores/useUIStore'
import { PropertiesPanel } from './components/PropertiesPanel'
import { ComponentsTree } from './components/ComponentsTree'
import { Button } from './components/ui/button'
import { Card, CardContent } from './components/ui/card'
import { ScrollArea } from './components/ui/scroll-area'
import { Separator } from './components/ui/separator'
import { fromSchema } from '@fabrick/store'
import { v4 as uuid } from 'uuid'
import { Trash2, Copy, Eye, EyeOff, Move, RotateCw, Expand, Grid3x3, MousePointer2 } from 'lucide-react'

const AppContent = observer(() => {
  const uiStore = useUIStore()
  const furnitureStore = useFurnitureStore()
  const [contextMenu, setContextMenu] = useState<{
    componentId: string
    x: number
    y: number
  } | null>(null)

  // Listen for right-click events from 3D components
  useEffect(() => {
    const handleRightClick = (e: CustomEvent) => {
      setContextMenu({
        componentId: e.detail.componentId,
        x: e.detail.clientX,
        y: e.detail.clientY,
      })
    }

    window.addEventListener('component-rightclick', handleRightClick as EventListener)
    return () => {
      window.removeEventListener('component-rightclick', handleRightClick as EventListener)
    }
  }, [])

  // Close context menu when clicking elsewhere
  useEffect(() => {
    const handleClick = () => {
      if (contextMenu) {
        setContextMenu(null)
      }
    }
    window.addEventListener('click', handleClick)
    return () => window.removeEventListener('click', handleClick)
  }, [contextMenu])

  // Keyboard shortcuts for transform modes
  React.useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      // Don't handle if typing in an input
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return
      }
      
      switch (e.key.toLowerCase()) {
        case 'g':
          furnitureStore.setTransformMode('translate')
          e.preventDefault()
          break
        case 'r':
          furnitureStore.setTransformMode('rotate')
          e.preventDefault()
          break
        case 's':
          furnitureStore.setTransformMode('scale')
          e.preventDefault()
          break
        case 'escape':
          furnitureStore.clearSelection()
          break
      }
    }
    
    window.addEventListener('keydown', handleKeyPress)
    return () => window.removeEventListener('keydown', handleKeyPress)
  }, [furnitureStore])

  const handleAddComponent = (componentType: string) => {
    const position: [number, number, number] = [
      Math.random() * 2 - 1, // Random x between -1 and 1
      0.5,
      Math.random() * 2 - 1  // Random z between -1 and 1
    ]
    
    let schema
    switch (componentType) {
      case 'cabinet':
        schema = SimpleCabinetFactory.createSchema(position)
        break
      case 'door':
        schema = Door.createSchema(position)
        break
      case 'drawer':
        schema = Drawer.createSchema(position)
        break
      case 'hardware':
        schema = Hardware.createSchema(position, { type: 'pull' })
        break
      case 'panel':
        schema = {
          id: uuid(),
          type: 'panel',
          geometry: { type: 'box', width: 0.4, height: 0.6, depth: 0.018 },
          material: { type: 'wood', subtype: 'OAK', color: '#D2B48C' },
          grain_direction: 'length',
          transform: {
            position: { x: position[0], y: position[1], z: position[2] },
            rotation: { x: 0, y: 0, z: 0, w: 1 },
            scale: { x: 1, y: 1, z: 1 }
          }
        }
        break
      case 'group':
        schema = {
          id: uuid(),
          type: 'group',
          name: 'New Group',
          layout: {
            transform: {
              position: { x: position[0], y: position[1], z: position[2] },
              rotation: { x: 0, y: 0, z: 0, w: 1 },
              scale: { x: 1, y: 1, z: 1 }
            }
          },
          children: {}
        }
        break
      default:
        return
    }
    
    const component = fromSchema(schema)
    furnitureStore.addComponent(component)
  }

  const selectedComponent = contextMenu
    ? furnitureStore.components.get(contextMenu.componentId)
    : null

  const handleDelete = () => {
    if (selectedComponent) {
      furnitureStore.removeComponent(selectedComponent.id)
    }
    setContextMenu(null)
  }

  const handleDuplicate = () => {
    if (selectedComponent) {
      furnitureStore.duplicateComponent(selectedComponent.id)
    }
    setContextMenu(null)
  }

  const handleToggleVisibility = () => {
    if (selectedComponent) {
      // Toggle visibility using the store method if available
      const panelStore = (furnitureStore as any).propertiesPanelStore
      if (panelStore && typeof panelStore.toggleComponentVisibility === 'function') {
        panelStore.toggleComponentVisibility(selectedComponent.id)
      } else {
        // Fallback: directly modify layout
        if (!selectedComponent.layout) {
          (selectedComponent as any).layout = {}
        }
        selectedComponent.layout.hidden = !selectedComponent.layout.hidden
      }
    }
    setContextMenu(null)
  }

  const isHidden = selectedComponent?.layout?.hidden || false

  return (
    <div className="flex h-screen w-full flex-col bg-background">
      {contextMenu && (
        <div
          className="fixed z-50 w-56 rounded-md border bg-popover p-1 text-popover-foreground shadow-md"
          style={{ 
            left: contextMenu.x, 
            top: contextMenu.y,
          }}
          onClick={(e) => e.stopPropagation()}
          onContextMenu={(e) => e.preventDefault()}
        >
          <div className="px-2 py-1.5 text-sm font-semibold text-foreground">
            {selectedComponent?.name || selectedComponent?.type || 'Component'}
          </div>
          <div className="-mx-1 my-1 h-px bg-border" />
          
          <div 
            className="relative flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground"
            onClick={handleDuplicate}
          >
            <Copy className="mr-2 h-4 w-4" />
            Duplicate
            <span className="ml-auto text-xs tracking-widest text-muted-foreground">Ctrl+D</span>
          </div>
          
          <div 
            className="relative flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground"
            onClick={handleToggleVisibility}
          >
            {isHidden ? (
              <>
                <Eye className="mr-2 h-4 w-4" />
                Show
              </>
            ) : (
              <>
                <EyeOff className="mr-2 h-4 w-4" />
                Hide
              </>
            )}
          </div>
          
          <div className="-mx-1 my-1 h-px bg-border" />
          
          <div 
            className="relative flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm text-destructive outline-none hover:bg-accent hover:text-destructive"
            onClick={handleDelete}
          >
            <Trash2 className="mr-2 h-4 w-4" />
            Delete
            <span className="ml-auto text-xs tracking-widest text-muted-foreground">Del</span>
          </div>
        </div>
      )}
            <header className="flex h-16 items-center justify-between border-b border-border bg-card px-5">
              <h1 className="text-lg font-semibold">Fabrick</h1>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm">New</Button>
                <Button variant="outline" size="sm">Save</Button>
                <Button variant="outline" size="sm">Export</Button>
              </div>
            </header>

      <div className="flex flex-1 overflow-hidden" style={{ height: 'calc(100vh - 64px)' }}>
        <aside style={{ width: `${uiStore.leftPanelWidth}px`, minWidth: 0 }} className="border-r border-border bg-card flex-shrink-0 relative overflow-hidden">
          <ScrollArea className="h-full w-full">
            <div className="p-5">
              <ComponentsTree />
              
              <Separator className="my-4" />
              
              <div className="mb-4">
                <h3 className="mb-3 text-sm font-semibold text-muted-foreground uppercase tracking-wide">Add Item</h3>
                <div className="flex flex-col gap-2">
                  <Card 
                    className="cursor-pointer transition-colors hover:bg-accent"
                    onClick={() => handleAddComponent('cabinet')}
                  >
                    <CardContent className="p-3">
                      <div className="text-sm font-medium">Cabinet</div>
                      <div className="text-xs text-muted-foreground">Full cabinet assembly</div>
                    </CardContent>
                  </Card>
                  <Card 
                    className="cursor-pointer transition-colors hover:bg-accent"
                    onClick={() => handleAddComponent('door')}
                  >
                    <CardContent className="p-3">
                      <div className="text-sm font-medium">Door</div>
                      <div className="text-xs text-muted-foreground">Cabinet door panel</div>
                    </CardContent>
                  </Card>
                  <Card 
                    className="cursor-pointer transition-colors hover:bg-accent"
                    onClick={() => handleAddComponent('drawer')}
                  >
                    <CardContent className="p-3">
                      <div className="text-sm font-medium">Drawer</div>
                      <div className="text-xs text-muted-foreground">Drawer box assembly</div>
                    </CardContent>
                  </Card>
                  <Card 
                    className="cursor-pointer transition-colors hover:bg-accent"
                    onClick={() => handleAddComponent('hardware')}
                  >
                    <CardContent className="p-3">
                      <div className="text-sm font-medium">Hardware</div>
                      <div className="text-xs text-muted-foreground">Hinges, pulls, handles</div>
                    </CardContent>
                  </Card>
                  <Card 
                    className="cursor-pointer transition-colors hover:bg-accent"
                    onClick={() => handleAddComponent('panel')}
                  >
                    <CardContent className="p-3">
                      <div className="text-sm font-medium">Panel</div>
                      <div className="text-xs text-muted-foreground">Simple panel component</div>
                    </CardContent>
                  </Card>
                  <Card 
                    className="cursor-pointer transition-colors hover:bg-accent"
                    onClick={() => handleAddComponent('group')}
                  >
                    <CardContent className="p-3">
                      <div className="text-sm font-medium">Group</div>
                      <div className="text-xs text-muted-foreground">Container for organizing components</div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </div>
          </ScrollArea>
          <div
            className="absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-primary/20 active:bg-primary/40 transition-colors z-10"
            onMouseDown={() => uiStore.startResizingLeft()}
          />
        </aside>

        <main className="flex-1 relative min-w-0">
          <Scene />
          {/* Bottom toolbar for transform controls */}
          <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 z-10">
            <div className="flex items-center gap-1 bg-card/90 backdrop-blur-sm border border-border rounded-lg px-2 py-1.5 shadow-lg">
              <Button
                variant={furnitureStore.transformMode === null ? 'default' : 'ghost'}
                size="icon"
                className="h-8 w-8"
                onClick={() => furnitureStore.setTransformMode(null)}
                title="Select Tool"
              >
                <MousePointer2 className="h-4 w-4" />
              </Button>
              <div className="w-px h-6 bg-border mx-1" />
              <Button
                variant={furnitureStore.transformMode === 'translate' ? 'default' : 'ghost'}
                size="icon"
                className="h-8 w-8"
                onClick={() => furnitureStore.setTransformMode('translate')}
                title="Move (G)"
              >
                <Move className="h-4 w-4" />
              </Button>
              <Button
                variant={furnitureStore.transformMode === 'rotate' ? 'default' : 'ghost'}
                size="icon"
                className="h-8 w-8"
                onClick={() => furnitureStore.setTransformMode('rotate')}
                title="Rotate (R)"
              >
                <RotateCw className="h-4 w-4" />
              </Button>
              <Button
                variant={furnitureStore.transformMode === 'scale' ? 'default' : 'ghost'}
                size="icon"
                className="h-8 w-8"
                onClick={() => furnitureStore.setTransformMode('scale')}
                title="Scale (S)"
              >
                <Expand className="h-4 w-4" />
              </Button>
              <div className="w-px h-6 bg-border mx-1" />
              <Button
                variant={furnitureStore.snapToGrid ? 'default' : 'ghost'}
                size="icon"
                className="h-8 w-8"
                onClick={() => furnitureStore.toggleSnapToGrid()}
                title="Snap to Grid"
              >
                <Grid3x3 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </main>

        <aside style={{ width: `${uiStore.rightPanelWidth}px`, minWidth: 0 }} className="border-l border-border flex-shrink-0 relative overflow-hidden">
          <PropertiesPanel />
          <div
            className="absolute top-0 left-0 w-1 h-full cursor-col-resize hover:bg-primary/20 active:bg-primary/40 transition-colors z-10"
            onMouseDown={() => uiStore.startResizingRight()}
          />
        </aside>
      </div>
    </div>
  )
})

function App() {
  return (
    <UIStoreProvider>
      <FurnitureStoreProvider>
        <PropertiesPanelStoreProvider>
          <AppContent />
        </PropertiesPanelStoreProvider>
      </FurnitureStoreProvider>
    </UIStoreProvider>
  )
}

export default App
